import { ApiClient, PaginatedResponse } from './apiClient';
import type { 
  Maquina,
  CreateMaquinaRequest,
  UpdateMaquinaRequest
} from '../types';

export class MaquinasService {
  /**
   * Obtiene lista paginada de máquinas
   */
  static async getMaquinas(
    page: number = 1,
    per_page: number = 10,
    search?: string
  ): Promise<PaginatedResponse<Maquina>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: per_page.toString()
      });
      
      if (search) {
        params.append('search', search);
      }

      // Usar método especializado para respuestas paginadas
      return await ApiClient.getPaginated<Maquina>(
        `/maquinas?${params.toString()}`
      );
      
    } catch (error: any) {
      console.error('❌ Error al obtener máquinas:', error);
      
      // Extraer mensaje de error más descriptivo
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error al obtener máquinas';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Obtiene una máquina por ID
   */
  static async getMaquina(id: number): Promise<Maquina> {
    try {
      // Usar método especializado para objetos individuales
      return await ApiClient.getOne<Maquina>(`/maquinas/${id}`);
      
    } catch (error: any) {
      console.error('❌ Error al obtener máquina:', error);
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error al obtener máquina';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Crea una nueva máquina
   */
  static async createMaquina(data: CreateMaquinaRequest): Promise<Maquina> {
    try {
      // POST retorna CreateUpdateResponse<Maquina> directamente
      const response = await ApiClient.post<Maquina>('/maquinas', data);
      
      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error(response.message || 'Error al crear máquina');
      
    } catch (error: any) {
      console.error('❌ Error al crear máquina:', error);
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error al crear máquina';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Actualiza una máquina
   */
  static async updateMaquina(id: number, data: UpdateMaquinaRequest): Promise<Maquina> {
    try {
      // PUT retorna CreateUpdateResponse<Maquina> directamente
      const response = await ApiClient.put<Maquina>(`/maquinas/${id}`, data);
      
      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error(response.message || 'Error al actualizar máquina');
      
    } catch (error: any) {
      console.error('❌ Error al actualizar máquina:', error);
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error al actualizar máquina';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Elimina una máquina
   */
  static async deleteMaquina(id: number): Promise<void> {
    try {
      // DELETE retorna DeleteResponse directamente
      const response = await ApiClient.delete(`/maquinas/${id}`);
      
      if (response.success) {
        return; // Eliminación exitosa
      }
      
      throw new Error(response.message || 'Error al eliminar máquina');
      
    } catch (error: any) {
      console.error('❌ Error al eliminar máquina:', error);
      
      // Extraer mensaje de error del backend
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error al eliminar máquina';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Obtiene lista simple de máquinas para dropdowns
   */
  static async obtenerListaMaquinas(): Promise<Maquina[]> {
    try {
      // Usar método especializado para arrays directos
      return await ApiClient.getList<Maquina>('/maquinas/lista');
      
    } catch (error: any) {
      console.error('❌ Error al obtener lista de máquinas:', error);
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error al obtener lista de máquinas';
      
      throw new Error(errorMessage);
    }
  }
}

export default MaquinasService;
